import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./Forms.module.css";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../UILibrary/TextInput";
import PasswordInput from "../UILibrary/PasswordInput";
import Submission from "../UILibrary/Submission";
import AlternativeAuth from "../../../Presentation/Components/UILibrary/AlternativeAuth";
import { Email } from "Utils/Global/regularExpressions";
import { defaultLoginValues } from "Utils/Global/globalConstants";
import type { ILoginFormData } from "@model/Authentication.model";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";
import { useNavigate } from "react-router-dom";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { CartService } from "Logic/CartService";
import { UserRegistrationStoreImplementation } from "Data/UserRegistration/UserRegistrationStoreImplementation";
import { UserCartStoreImplementation } from "Data/Cart/CartStoreImplementation";
import { AuthBannerStoreImplementation } from "Data/AuthBanner/AuthBannerStoreImplementation";
import Close from "Utils/Assets/SVGs/Close";
import { useNavigationRedirection } from "../../../Logic/Functions/Hooks/useNavigationRedirection";

const LOGIN_LIMIT = Number(process.env.REACT_APP_LOGIN_LIMIT ?? 5);

const LoginForm: React.FC = () => {
  const { control, handleSubmit, reset, watch } = useForm<ILoginFormData>({
    mode: "onSubmit",
    defaultValues: defaultLoginValues,
  });

  //  Global auth banner (forced logout)
  const { bannerMessage, bannerReturnPath, clearBanner } = AuthBannerStoreImplementation();
  //  Regular API error (wrong credentials, validation, etc.)
  const [apiError, setApiErrorState] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [loginFailedCount, setLoginFailedCount] = useState(0);
  const [tooManyFailedAttempts, setTooManyFailedAttempts] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const navigate = useNavigate();
  const { preservedReturnPath } = useNavigationRedirection({
    disallowStartsWith: ["/auth", "/login"],
  });
  const userAuthenticationStore = UserAuthenticationStoreImplementation();
  const userRegistrationStore = UserRegistrationStoreImplementation();

  const authRef = useRef<AuthenticationService>(new AuthenticationService());
  const cartServiceRef = useRef<CartService>(new CartService());

  const { itemsICart: guestItemsICart, clearCart: clearReduxCart } =
    UserCartStoreImplementation(
      "USD",
      cartServiceRef.current.mapCartStateToICartItems,
      cartServiceRef.current.computeCartSubtotalFromICartItems
    );

  const reconciledForTokenRef = useRef<string | null>(null);
  const emailValue = watch("email");

  useEffect(() => {
    setTooManyFailedAttempts(loginFailedCount >= LOGIN_LIMIT);
  }, [loginFailedCount]);

  useEffect(() => {
    setNeedsVerification(false);
    setApiErrorState(undefined);
  }, [emailValue]);

  const onValidLogin = useCallback(
    async (form: ILoginFormData) => {
      setNeedsVerification(false);

      const token = await authRef.current.handleLogin({
        data: form,
        reset,
        setLoginFailedCount,
        handleUserData: userAuthenticationStore.logUserIn,
        setApiError: (msg: string | null) => {
          const text = msg ?? "";
          setApiErrorState(text || undefined);
          setNeedsVerification((text || "").toLowerCase().includes("verify"));
        },
        setLoading,
      });

      if (!token) return;

      if (
        reconciledForTokenRef.current !== token &&
        guestItemsICart.length > 0
      ) {
        try {
          await cartServiceRef.current.handleReconcileGuestCart({
            token,
            guestItems: guestItemsICart,
            setLoading: () => { },
            setApiError: () => { },
            setItems: () => { },
            onAuthError: () => { },
          });
          clearReduxCart();
          reconciledForTokenRef.current = token;
          window.dispatchEvent(new CustomEvent("cart:changed"));
        } catch {
        }
      }

      const safeReturnPath = authRef.current.getSafeReturnPath({
        bannerReturnPath,
        preservedReturnPath,
      });

      clearBanner();
      navigate(safeReturnPath, { replace: true });
    },
    [
      guestItemsICart.length,
      clearReduxCart,
      navigate,
      userAuthenticationStore.logUserIn,
      reset,
      preservedReturnPath,
      bannerReturnPath,
      clearBanner,
    ]
  );

  const updateAPIErrorState = (m: string | null) => {
    setApiErrorState(m ?? undefined);
  };

  const handleVerifyAndResend = useCallback(async () => {
    const email = (emailValue ?? "").trim();
    if (!email) {
      setApiErrorState("Please enter your email first.");
      return;
    }

    const msg = await authRef.current.handleResendVerification({
      email,
      isNew: false,
      navigate: false,
      setApiError: updateAPIErrorState,
      setLoading: setResendLoading,
    });

    if (msg) {
      userRegistrationStore?.verifyUser({
        verification: {
          email,
          isNew: false,
        },
      });
      navigate(`/auth/verification`, { replace: true });
    }
  }, [emailValue, navigate, userRegistrationStore]);

  const handlePrimaryAction = needsVerification
    ? handleVerifyAndResend
    : handleSubmit(onValidLogin);

  const primaryButtonText = needsVerification
    ? resendLoading
      ? "Sending code..."
      : "Verify Account"
    : loading
    ? "Logging in..."
    : "Login";

  const handleGoogleLogin = () => console.log("Google Sign In");
  const handleAppleLogin = () => console.log("Apple Sign In");
  const handleForgotPassword = () => navigate("/auth/forget-password");
  const handleSignup = () => navigate("/auth/signup");

  return (
    <div className={styles.pageWrapper}>
       <div className={styles.loginStack}>
      {bannerMessage && (
        <div className={styles.loginBanner} role="status">
          <span className={styles.loginBannerText}>{bannerMessage}</span>
          <button
            type="button"
            className={styles.loginBannerClose}
            onClick={clearBanner}
            aria-label="Dismiss message"
          >
            <Close />
          </button>
        </div>
      )}

      <div className={styles.LoginContainer}>
        <div className={styles.loginImage} />

        <div className={styles.loginForm}>
          <form
            data-testid="login_form"
            className={styles.formContainer}
            onSubmit={handleSubmit(onValidLogin)}
          >
            <h2 className={styles.LoginTitle}>Welcome Back</h2>
            <h3 className={styles.LoginTitle}>{apiError}</h3>

            <div className="margin-bottom-10">
              <Controller
                name="email"
                control={control}
                rules={{
                  required: { value: true, message: "Email is required!" },
                  pattern: { value: Email, message: "Invalid email format!" },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label="Email"
                    placeholder="Enter your email"
                    type="email"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <Controller
              name="password"
              control={control}
              rules={{ required: "Password is required" }}
              render={({ field, fieldState }) => (
                <PasswordInput
                  label="Password"
                  placeholder="Enter your password"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            <div className="margin-bottom-20">
              <div className={styles.navigationText}>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className={styles.navigationLink}
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {tooManyFailedAttempts && (
              <div className={styles.formError} data-testid="lockout-msg">
                Too many failed attempts. Please try again later.
              </div>
            )}

            <Submission
              error={apiError}
              disabled={loading || resendLoading || tooManyFailedAttempts}
              buttonText={primaryButtonText}
              onClick={handlePrimaryAction}
            />

            <div className={styles.redirectLogin}>
              <span className={styles.navigationText}>
                Don't Have an Account?
              </span>
              <button
                type="button"
                onClick={handleSignup}
                className={styles.navigationLink}
              >
                Sign up
              </button>
            </div>

            {/* <AlternativeAuth
              message="Or Login with"
              handleGoogleAction={handleGoogleLogin}
              handleAppleAction={handleAppleLogin}
            /> */}
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
